import styled, { css } from 'styled-components';

import {
  COLORS,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
  HOME,
  HOME_STICKY_HEAD,
  REDUCED_MOTION,
} from '../../theme/tokens';

export type PinnedBarPlacement = 'page' | 'dialog';

/** How far the pill reaches past the bar by default (the Stats header's: 10px above and below, 8px either side). In
 *  a dialog the bar pins this far down its scroll area, so the pill is not cut off at its edge. */
export const PILL_REACH_Y_PX = 10;
const PILL_REACH_X_PX = 8;

/* What else is pinned above the bar: a consumer sets `--pinned-bar-offset` (a length, on the bar or an ancestor, per
   breakpoint as it needs) and the bar pins that much lower. Where the pill's edges are, from the bar's, is
   `--pinned-bar-pill-top`, `--pinned-bar-pill-x` (either side) and `--pinned-bar-pill-bottom` (as CSS `inset` lengths:
   negative reaches out past the bar), for content that brings its own frame. */
const OFFSET = 'var(--pinned-bar-offset, 0px)';
const PILL_TOP = `var(--pinned-bar-pill-top, -${PILL_REACH_Y_PX}px)`;
const PILL_X = `var(--pinned-bar-pill-x, -${PILL_REACH_X_PX}px)`;
const PILL_BOTTOM = `var(--pinned-bar-pill-bottom, -${PILL_REACH_Y_PX}px)`;

/* How far down the bar the Stats header's bridge reaches, and so, on the page, how far below the top of the scroll area
   its top edge stays (its height is the pin's, from there). */
const BRIDGE_FLOOR = '14px';

/* How far down the bar the bridge reaches: under the pill's rounded top corners (its top edge plus its radius), so
   nothing shows beside them, and never less than the Stats header's BRIDGE_FLOOR. */
const BRIDGE_INTO = `max(${BRIDGE_FLOOR}, ${PILL_TOP} + ${HOME_STICKY_HEAD.pillRadius})`;

const morph = `${HOME_STICKY_HEAD.morphMs}ms ${HOME.ease}`;

/*
 * The bar: sticky, under the layout top bar on the page (the same offsets as BackHeader and the Stats header: on a
 * desktop the support-impersonation strip is already in the scrollport's own offset, on a phone it is not), or at the
 * top of the scroll area it sits in, in a dialog. Once pinned (`$stuck`) it lifts into a floating pill (an opaque
 * surface, rounded corners, a soft shadow), over a solid fill bridging the band up to the top bar so nothing scrolling
 * up shows in the gap. Both are drawn on pseudos behind the content, so the content never moves as they appear; the
 * bar's own stacking context (z-index 5) keeps even those negative layers over what scrolls beneath it.
 */
export const Root = styled.div<{ $placement: PinnedBarPlacement; $bridge: string; $stuck: boolean }>`
  position: sticky;
  z-index: 5;
  top: ${({ $placement }) =>
    $placement === 'dialog'
      ? `calc(${PILL_REACH_Y_PX}px + ${OFFSET})`
      : `calc(${HEADER_DESKTOP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + ${OFFSET})`};

  /* The fill bridging up to the top bar (the scroll area's top, in a dialog), from under the pill's top corners
     (BRIDGE_INTO). On the page it stops where the Stats header's always has, 14px below the top of the scroll area, under
     the top bar. A layer below the pill, so it never paints over the pill's rounded top; it only masks what scrolls
     behind. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% - ${BRIDGE_INTO});
    height: ${({ $placement }) =>
      $placement === 'dialog'
        ? `calc(${PILL_REACH_Y_PX}px + ${OFFSET} + ${BRIDGE_INTO})`
        : `calc(${HEADER_DESKTOP_PX}px + env(safe-area-inset-top) + ${OFFSET} + ${BRIDGE_INTO} - ${BRIDGE_FLOOR})`};
    z-index: -2;
    background: ${({ $bridge }) => $bridge};
    opacity: ${({ $stuck }) => ($stuck ? 1 : 0)};
    transition: opacity ${morph};
    /* Shown, it takes the taps too: what it hides is not to be reached through it. */
    pointer-events: ${({ $stuck }) => ($stuck ? 'auto' : 'none')};
  }

  /* The pill. */
  &::after {
    content: '';
    position: absolute;
    inset: ${PILL_TOP} ${PILL_X} ${PILL_BOTTOM};
    z-index: -1;
    box-sizing: border-box;
    border-radius: ${HOME_STICKY_HEAD.pillRadius};
    background: ${HOME_STICKY_HEAD.pillBg};
    border: 1px solid ${HOME_STICKY_HEAD.pillBorder};
    box-shadow: ${HOME_STICKY_HEAD.pillShadow};
    opacity: ${({ $stuck }) => ($stuck ? 1 : 0)};
    transform: ${({ $stuck }) => ($stuck ? 'none' : 'translateY(-6px) scale(0.985)')};
    transform-origin: top center;
    transition:
      opacity ${morph},
      transform ${morph};
    pointer-events: ${({ $stuck }) => ($stuck ? 'auto' : 'none')};
  }

  ${({ $placement }) =>
    $placement === 'page' &&
    css`
      ${FIXED_HEADER} {
        top: calc(
          ${HEADER_MOBILE_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px) +
            ${OFFSET}
        );

        &::before {
          height: calc(${HEADER_MOBILE_PX}px + env(safe-area-inset-top) + ${OFFSET} + ${BRIDGE_INTO} - ${BRIDGE_FLOOR});
        }
      }
    `}

  ${REDUCED_MOTION} {
    &::before,
    &::after {
      transition: none;
    }
  }
`;

/** The page's background, the bridge's default fill. */
export const PAGE_BRIDGE = COLORS.pageBg;
